// Stellar SDK packages are loaded lazily so Soroban support stays out of the
// initial application bundle while Vite can still split them into vendor chunks.
//
// import { isConnected, getAddress } from "@stellar/freighter-api";
// import { rpc } from "@stellar/stellar-sdk";

// ---------------------------------------------------------------------------
// Freighter wallet-change subscription types
// ---------------------------------------------------------------------------
export interface StellarWalletChangeEvent {
  account?: string;
  network?: string;
}

export type StellarWalletChangeListener = (event: StellarWalletChangeEvent) => void;

export type StellarUnsubscribe = () => void;

export interface StellarKeeperAuthorizationData {
  keeper: string;
  expiresAt: number;
}

const normalizeAddressValue = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (value && typeof (value as { address?: unknown }).address === "string") {
    return (value as { address: string }).address.trim();
  }
  return "";
};

const normalizeNetworkValue = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (value && typeof (value as { network?: unknown }).network === "string") {
    return (value as { network: string }).network.trim();
  }
  return "";
};

// ---------------------------------------------------------------------------
// Lightweight freighter shim – replaced by real API when package is present
// ---------------------------------------------------------------------------
export type FreighterShim = {
  isConnected: () => Promise<boolean>;
  getAddress: () => Promise<string>;
  getPublicKey?: () => Promise<string>;
  isAllowed?: () => Promise<boolean>;
  setAllowed?: () => Promise<boolean>;
  requestAccess?: () => Promise<string>;
  getUserInfo?: () => Promise<{ publicKey?: string }>;
  signTransaction?: (xdr: string, opts?: any) => Promise<string>;
  signAuthEntry?: (preimageXdr: string, opts?: any) => Promise<{ signedAuthEntry: string; error?: string }>;
  signBlob?: (blob: string, opts?: any) => Promise<string>;
  getNetwork?: () => Promise<unknown>;
  listen?: (callback: (event: StellarWalletChangeEvent) => void) => unknown;
};

let _freighter: any = null;
let _stellarSdk: any = null;

export const setMockFreighter = (mock: any) => {
  _freighter = mock;
};

export const setMockStellarSdk = (mock: any) => {
  _stellarSdk = mock;
};

const loadFreighter = async (): Promise<FreighterShim> => {
  if (_freighter) return _freighter;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mod = await import("@stellar/freighter-api") as any;
    _freighter = {
      isConnected: async () => {
        try {
          const result = await mod.isConnected();
          return typeof result === "boolean" ? result : Boolean(result?.isConnected);
        } catch {
          return false;
        }
      },
      isAllowed: async () => {
        try {
          if (typeof mod.isAllowed === "function") {
            const res = await mod.isAllowed();
            return typeof res === "boolean" ? res : Boolean(res?.isAllowed);
          }
          return false;
        } catch {
          return false;
        }
      },
      setAllowed: async () => {
        try {
          if (typeof mod.setAllowed === "function") {
            const res = await mod.setAllowed();
            return typeof res === "boolean" ? res : Boolean(res?.isAllowed);
          }
          return false;
        } catch {
          return false;
        }
      },
      requestAccess: typeof mod.requestAccess === "function" ? mod.requestAccess : undefined,
      getPublicKey: typeof mod.getPublicKey === "function" ? mod.getPublicKey : undefined,
      getUserInfo: typeof mod.getUserInfo === "function" ? mod.getUserInfo : undefined,
      getAddress: async () => {
        try {
          if (typeof mod.getPublicKey === "function") {
            const pk = await mod.getPublicKey();
            const normalized = normalizeAddressValue(pk);
            if (normalized) return normalized;
          }
          if (typeof mod.getUserInfo === "function") {
            const info = await mod.getUserInfo();
            if (info?.publicKey) return normalizeAddressValue(info.publicKey);
          }
          return "";
        } catch {
          return "";
        }
      },
      signTransaction: mod.signTransaction,
      signAuthEntry: mod.signAuthEntry,
      signBlob: typeof mod?.signBlob === "function" ? mod.signBlob : undefined,
      getNetwork: typeof mod?.getNetwork === "function" ? mod.getNetwork : undefined,
      listen: typeof mod?.listen === "function" ? mod.listen : undefined,
    };
  } catch {
    // Package not installed – graceful fallback stubs
    _freighter = {
      isConnected: async () => false,
      getAddress: async () => "",
      signTransaction: async () => "",
      signAuthEntry: async () => ({ signedAuthEntry: "" }),
      signBlob: async () => "",
    };
  }
  return _freighter;
};

const loadStellarSdk = async (): Promise<any> => {
  if (_stellarSdk) return _stellarSdk;
  try {
    _stellarSdk = await import("@stellar/stellar-sdk");
  } catch {
    throw new Error("Stellar SDK is not installed or failed to load");
  }
  return _stellarSdk;
};

export interface StellarVaultData {
  id: number;
  creator: string;
  name: string;
  description: string;
  guardians: string[];
  approvalThreshold: number;
  isActive: boolean;
  createdAt: number;
  network?: "avalanche" | "stellar";
}

export interface StellarTokenData {
  tokenId: number;
  owner: string;
  vaultId: number | null;
  tokenURI: string;
  mintedAt: number | null;
}

export interface StellarDocumentData {
  id: number;
  vaultId: number;
  encryptedMetadata: string;
  ipfsHash: string;
  uploadedBy: string;
  uploadedAt: number;
  requiredAccess: number;
}

export interface StellarKeeperAuthorizationData {
  keeper: string;
  expiresAt: number;
}

export interface StellarPendingApprovalData {
  requestId: number;
  documentId: number;
  vaultId: number;
  vaultName: string;
  requester: string;
  createdAt: number;
  expiresAt: number;
}

let activeAccount: string | null = null;
const sorobanRpcUrl = "https://soroban-testnet.stellar.org";
let contractId = "";

export const getRpcUrl = (): string => {
  const url = import.meta.env.VITE_STELLAR_RPC_URL as string | undefined;
  return url || sorobanRpcUrl;
};

export const getContractId = (): string => {
  const cid = import.meta.env.VITE_STELLAR_CONTRACT_ADDRESS as string | undefined;
  return cid || contractId || "";
};

// ---------------------------------------------------------------------------
// Cross-chain identity binding registry (issue #131)
// ---------------------------------------------------------------------------
// The Soroban `identity_registry` module (and the EVM
// `CrossChainIdentityRegistry.sol` contract) verify BOTH the Stellar
// (Ed25519) and EVM (secp256k1) signatures of
// `BindIdentity(evmAddr, stellarPubkey, timestamp)` on-chain before recording
// a binding. These helpers build and sign the shared payload.

const BIND_IDENTITY_PREFIX = "BindIdentity";

let identityRegistryContractId = "";

export const getIdentityRegistryContractId = (): string => {
  const cid = import.meta.env.VITE_IDENTITY_REGISTRY_ADDRESS as string | undefined;
  return cid || identityRegistryContractId;
};

const isIdentityRegistryConfigured = (): boolean => !!getIdentityRegistryContractId();

const hexToBytes = (hex: string): Uint8Array => {
  const clean = hex.replace(/^0x/i, "");
  if (clean.length % 2 !== 0) throw new Error("Invalid hex string");
  const bytes = new Uint8Array(clean.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(clean.substring(i * 2, i * 2 + 2), 16);
  }
  return bytes;
};

const bytesToHex = (bytes: Uint8Array | ArrayBuffer): string => {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
};

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

const base64ToBytes = (b64: string): Uint8Array => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
};

const loadEthers = async (): Promise<any> => {
  try {
    return await import("ethers");
  } catch {
    throw new Error("ethers is not installed or failed to load");
  }
};

const loadTweetNacl = async (): Promise<any> => {
  try {
    return await import("tweetnacl");
  } catch {
    throw new Error("tweetnacl is not installed or failed to load");
  }
};

/**
 * Build the 72-byte payload that both wallets sign:
 * "BindIdentity" || evmAddress(20) || stellarPublicKey(32) || timestamp(8, BE)
 */
const buildIdentityBindingPayload = (
  evmAddressBytes: Uint8Array,
  stellarPublicKeyBytes: Uint8Array,
  timestamp: number
): Uint8Array => {
  const payload = new Uint8Array(12 + 20 + 32 + 8);
  payload.set(new TextEncoder().encode(BIND_IDENTITY_PREFIX), 0);
  payload.set(evmAddressBytes, 12);
  payload.set(stellarPublicKeyBytes, 32);
  new DataView(payload.buffer).setBigUint64(64, BigInt(Math.floor(timestamp)), false);
  return payload;
};

/**
 * Compute the 32-byte message hash both wallets sign for a binding.
 * @returns 0x-prefixed hex of keccak256(payload)
 */
const buildIdentityBindingMessageHash = async (
  evmAddress: string,
  stellarAddress: string,
  timestamp: number
): Promise<string> => {
  const sdk = await loadStellarSdk();
  const stellarPubkey = sdk.StrKey.decodeEd25519PublicKey(stellarAddress);
  const evmBytes = hexToBytes(evmAddress);
  if (evmBytes.length !== 20) throw new Error("Invalid EVM address");
  const payload = buildIdentityBindingPayload(evmBytes, Uint8Array.from(stellarPubkey), timestamp);
  const ethersMod = await loadEthers();
  return ethersMod.keccak256(payload);
};

/**
 * Encode a 32-byte Ed25519 public key as a Stellar G-address.
 */
const encodeStellarPublicKey = async (publicKeyHex: string): Promise<string> => {
  const sdk = await loadStellarSdk();
  const bytes = hexToBytes(publicKeyHex);
  if (bytes.length !== 32) throw new Error("Invalid Stellar public key length");
  return sdk.StrKey.encodeEd25519PublicKey(bytes);
};

/**
 * Normalize the ScVal struct returned by the Soroban identity registry into a
 * plain JS object. Bytes fields come back as Buffers from scValToNative.
 */
const normalizeBindingResult = (result: any): { evmAddress: string; stellarPublicKey: string; timestamp: number } | null => {
  if (!result || typeof result !== "object") return null;
  const rawEvm = result?.evm_address ?? result?.evmAddress ?? null;
  const rawPk = result?.stellar_pubkey ?? result?.stellarPublicKey ?? null;
  const rawTs = result?.timestamp ?? null;
  if (rawPk === null || rawTs === null) return null;
  const toHexString = (v: any): string => {
    if (typeof v === "string") return v.startsWith("0x") ? v : `0x${v}`;
    if (v instanceof Uint8Array || (typeof Buffer !== "undefined" && Buffer.isBuffer(v))) {
      return `0x${bytesToHex(v as Uint8Array)}`;
    }
    return "";
  };
  return {
    evmAddress: toHexString(rawEvm),
    stellarPublicKey: toHexString(rawPk),
    timestamp: Number(rawTs),
  };
};

export type WalletProviderType = "freighter" | "demo" | "albedo";
let activeWalletType: WalletProviderType = "freighter";
let demoKeypair: any = null;

export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const getWalletType = (): WalletProviderType => activeWalletType;
export const getDemoKeypair = (): any => demoKeypair;

const isConfigured = (): boolean => {
  return !!getContractId();
};

const initialize = async (customContractId?: string): Promise<string | null> => {
  if (customContractId) {
    contractId = customContractId;
  } else {
    contractId = (import.meta.env.VITE_STELLAR_CONTRACT_ADDRESS as string | undefined) || "";
  }

  // 1. Check stored wallet session
  if (typeof window !== "undefined") {
    const savedType = window.localStorage.getItem("spoovault_wallet_type") as WalletProviderType | null;
    if (savedType === "demo") {
      const savedSecret = window.localStorage.getItem("spoovault_demo_secret");
      if (savedSecret && savedSecret.startsWith("S")) {
        try {
          const sdk = await loadStellarSdk();
          demoKeypair = sdk.Keypair.fromSecret(savedSecret);
          activeAccount = demoKeypair.publicKey();
          activeWalletType = "demo";
          activeNetwork = "TESTNET";
          return activeAccount;
        } catch (e) {
          console.warn("Failed to restore demo keypair:", e);
        }
      }
    } else if (savedType === "albedo") {
      const savedAccount = window.localStorage.getItem("spoovault_account");
      if (savedAccount) {
        activeAccount = savedAccount;
        activeWalletType = "albedo";
        activeNetwork = "TESTNET";
        return activeAccount;
      }
    }
  }

  // 2. Try Freighter
  try {
    const freighter = await loadFreighter();
    const connected = await freighter.isConnected();
    if (connected) {
      let allowed = true;
      if (typeof freighter.isAllowed === "function") {
        allowed = await freighter.isAllowed().catch(() => false);
      }
      if (allowed) {
        const address = await freighter.getAddress();
        activeAccount = address || null;
        activeWalletType = "freighter";
        return activeAccount;
      }
    }
  } catch (error) {
    console.error("Freighter initialization failed:", error);
  }
  return null;
};

const clear = () => {
  activeAccount = null;
  activeNetwork = "";
  demoKeypair = null;
  activeWalletType = "freighter";
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("spoovault_wallet_type");
    window.localStorage.removeItem("spoovault_account");
  }
};

const getAccount = (): string | null => activeAccount;

export const connectDemoWallet = async (providedSecret?: string): Promise<string> => {
  const sdk = await loadStellarSdk();
  let keypair: any;
  if (providedSecret && providedSecret.startsWith("S")) {
    keypair = sdk.Keypair.fromSecret(providedSecret.trim());
  } else {
    const storedSecret = typeof window !== "undefined" ? window.localStorage.getItem("spoovault_demo_secret") : null;
    if (storedSecret && storedSecret.startsWith("S")) {
      try {
        keypair = sdk.Keypair.fromSecret(storedSecret);
      } catch {
        keypair = sdk.Keypair.random();
      }
    } else {
      keypair = sdk.Keypair.random();
    }
  }

  const pubKey = keypair.publicKey();
  if (typeof window !== "undefined") {
    window.localStorage.setItem("spoovault_demo_secret", keypair.secret());
    window.localStorage.setItem("spoovault_wallet_type", "demo");
    window.localStorage.setItem("spoovault_account", pubKey);
  }

  demoKeypair = keypair;
  activeAccount = pubKey;
  activeWalletType = "demo";
  activeNetwork = "TESTNET";

  // Auto-fund via Friendbot on testnet in background
  try {
    void fetch(`https://friendbot.stellar.org/?addr=${pubKey}`).catch(() => {});
  } catch {
    // ignore
  }

  return pubKey;
};

export const connectAlbedoWallet = async (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      return reject(new Error("Window is not available"));
    }
    const width = 450;
    const height = 650;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      "https://albedo.link/confirm?intent=public_key",
      "albedo-auth",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      return reject(new Error("Popup was blocked by your browser. Please allow popups for Albedo authentication."));
    }

    const timer = setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("Albedo connection timed out"));
    }, 120000);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== "https://albedo.link") return;
      if (event.data && event.data.albedo) {
        clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        const { albedo } = event.data;
        if (albedo.error) {
          reject(new Error(albedo.error.message || "Albedo authentication rejected"));
        } else if (albedo.pubkey) {
          activeAccount = albedo.pubkey;
          activeWalletType = "albedo";
          activeNetwork = "TESTNET";
          window.localStorage.setItem("spoovault_wallet_type", "albedo");
          window.localStorage.setItem("spoovault_account", albedo.pubkey);
          resolve(albedo.pubkey);
        } else {
          reject(new Error("No public key returned by Albedo"));
        }
      }
    };

    window.addEventListener("message", onMessage);
  });
};

const requestAlbedoTransaction = async (xdr: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      return reject(new Error("Window is not available"));
    }
    const width = 500;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(
      `https://albedo.link/confirm?intent=tx&xdr=${encodeURIComponent(xdr)}&network=testnet`,
      "albedo-tx",
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      return reject(new Error("Popup blocked. Please allow popups for Albedo transaction signing."));
    }

    const timer = setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("Albedo transaction signing timed out"));
    }, 180000);

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== "https://albedo.link") return;
      if (event.data && event.data.albedo) {
        clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        const { albedo } = event.data;
        if (albedo.error) {
          reject(new Error(albedo.error.message || "Albedo transaction rejected"));
        } else {
          const signed = albedo.signed_envelope_xdr || albedo.xdr;
          if (signed) {
            resolve(signed);
          } else {
            reject(new Error("No signed transaction XDR returned"));
          }
        }
      }
    };

    window.addEventListener("message", onMessage);
  });
};

const connectWallet = async (): Promise<string> => {
  const freighter = await loadFreighter();
  const connected = await freighter.isConnected();
  if (!connected) {
    throw new Error(
      "Freighter wallet extension is not installed or enabled. Please install Freighter from https://www.freighter.app"
    );
  }

  let address = "";

  // 1. Try requestAccess() first - this triggers the Freighter authorization popup
  if (typeof freighter.requestAccess === "function") {
    try {
      const accessResult = await freighter.requestAccess();
      if (accessResult) {
        address = normalizeAddressValue(accessResult);
      }
    } catch (err: any) {
      console.warn("Freighter requestAccess threw:", err);
      const msg = typeof err?.message === "string" ? err.message.toLowerCase() : "";
      if (msg.includes("user rejected") || msg.includes("user denied") || msg.includes("reject")) {
        throw new Error("Connection request rejected in Freighter wallet");
      }
    }
  }

  // 2. If requestAccess didn't return an address, try setAllowed()
  if (!address && typeof freighter.setAllowed === "function") {
    try {
      await freighter.setAllowed();
    } catch (err: any) {
      console.warn("Freighter setAllowed threw:", err);
      const msg = typeof err?.message === "string" ? err.message.toLowerCase() : "";
      if (msg.includes("user rejected") || msg.includes("user denied") || msg.includes("reject")) {
        throw new Error("Connection request rejected in Freighter wallet");
      }
    }
  }

  // 3. Retrieve public key
  if (!address) {
    address = await freighter.getAddress();
  }

  if (!address && typeof freighter.getPublicKey === "function") {
    try {
      address = normalizeAddressValue(await freighter.getPublicKey());
    } catch (err) {
      console.warn("Freighter getPublicKey threw:", err);
    }
  }

  if (!address) {
    throw new Error(
      "Failed to get address from Freighter wallet. Please ensure Freighter is unlocked with an active account."
    );
  }

  activeAccount = address;
  activeWalletType = "freighter";
  if (typeof window !== "undefined") {
    window.localStorage.setItem("spoovault_wallet_type", "freighter");
    window.localStorage.setItem("spoovault_account", address);
  }
  return address;
};

// Fallback Mock Storage for local development and test environments when Freighter/Soroban is not deployed
const inMemoryMockStorage: Record<string, string> = {};

// ---------------------------------------------------------------------------
// Freighter wallet-change subscriptions
//
// The frontend previously snapshotted the Freighter wallet state once during
// initialization. If the user switched accounts or changed networks inside the
// Freighter extension, the app stayed out of sync and subsequent Soroban
// transactions were signed for the wrong account (HostError Auth/InvalidAction).
//
// We integrate Freighter's `listen` event subscription when it is available
// (either from the npm package or the injected window.freighterApi global) and
// fall back to a lightweight polling watcher so account/network changes are
// always detected even without native event support.
// ---------------------------------------------------------------------------

let activeNetwork = "";

const getActiveNetwork = (): string => activeNetwork;

const getNetworkValue = async (freighter: FreighterShim): Promise<string> => {
  if (typeof freighter.getNetwork !== "function") return "";
  try {
    return normalizeNetworkValue(await freighter.getNetwork());
  } catch {
    return "";
  }
};

/**
 * Resolve the current Freighter network name (e.g. "TESTNET" or "PUBLIC").
 */
const getNetwork = async (): Promise<string> => {
  const freighter = await loadFreighter();
  const network = await getNetworkValue(freighter);
  if (network) {
    activeNetwork = network;
  }
  return network || activeNetwork;
};

const getInjectedListen = (): ((callback: (event: StellarWalletChangeEvent) => void) => unknown) | null => {
  if (typeof window === "undefined") return null;
  const injected = window.freighterApi || window.freighter;
  if (injected && typeof injected.listen === "function") {
    return injected.listen;
  }
  return null;
};

const loadListen = async (): Promise<((callback: (event: StellarWalletChangeEvent) => void) => unknown) | null> => {
  const freighter = await loadFreighter();
  if (typeof freighter.listen === "function") {
    return freighter.listen;
  }
  return getInjectedListen();
};

const startPollingWatcher = (listener: StellarWalletChangeListener): (() => void) => {
  if (typeof window === "undefined" || typeof window.setInterval !== "function") {
    return () => {};
  }

  let lastAccount = activeAccount ?? "";
  let lastNetwork = activeNetwork;

  const tick = async () => {
    try {
      const freighter = await loadFreighter();
      const [account, network] = await Promise.all([
        freighter.getAddress(),
        getNetworkValue(freighter),
      ]);
      const next: StellarWalletChangeEvent = {};
      if (account && account !== lastAccount) {
        lastAccount = account;
        activeAccount = account;
        next.account = account;
      }
      if (network && network !== lastNetwork) {
        lastNetwork = network;
        activeNetwork = network;
        next.network = network;
      }
      if (next.account || next.network) {
        listener(next);
      }
    } catch {
      // ignore transient polling failures
    }
  };

  void tick();
  const interval = window.setInterval(tick, 2000);
  return () => window.clearInterval(interval);
};

/**
 * Subscribe to Freighter account/network changes. Prefers the extension's
 * native `listen` subscription; otherwise falls back to polling. Returns an
 * unsubscribe function that should be invoked on cleanup.
 */
export const subscribeToWalletChanges = (
  listener: StellarWalletChangeListener
): StellarUnsubscribe => {
  let active = true;
  let stopNative: (() => void) | null = null;
  let stopPolling: (() => void) | null = null;

  const handleEvent = (event: StellarWalletChangeEvent) => {
    if (!active) return;
    if (event && typeof event.account === "string" && event.account) {
      activeAccount = event.account;
    }
    if (event && typeof event.network === "string" && event.network) {
      activeNetwork = event.network;
    }
    listener({
      ...(event && typeof event.account === "string" && event.account
        ? { account: event.account }
        : {}),
      ...(event && typeof event.network === "string" && event.network
        ? { network: event.network }
        : {}),
    });
  };

  void (async () => {
    try {
      const listenFn = await loadListen();
      if (!active) return;
      if (listenFn) {
        const ret = listenFn(handleEvent) as unknown;
        stopNative = typeof ret === "function" ? (ret as () => void) : () => {};
      } else {
        stopPolling = startPollingWatcher(handleEvent);
      }
    } catch {
      if (active) {
        stopPolling = startPollingWatcher(handleEvent);
      }
    }
  })();

  return () => {
    active = false;
    if (stopNative) stopNative();
    if (stopPolling) stopPolling();
  };
};

const getMockStorage = <T,>(key: string, defaults: T): T => {
  try {
    if (typeof localStorage !== "undefined" && localStorage !== null) {
      const raw = localStorage.getItem(`spoovault-stellar-mock-${key}`);
      return raw ? (JSON.parse(raw) as T) : defaults;
    }
  } catch {
    // ignore and fallback
  }
  const raw = inMemoryMockStorage[`spoovault-stellar-mock-${key}`];
  return raw ? (JSON.parse(raw) as T) : defaults;
};

const saveMockStorage = <T,>(key: string, data: T) => {
  const jsonStr = JSON.stringify(data);
  try {
    if (typeof localStorage !== "undefined" && localStorage !== null) {
      localStorage.setItem(`spoovault-stellar-mock-${key}`, jsonStr);
      return;
    }
  } catch {
    // ignore
  }
  inMemoryMockStorage[`spoovault-stellar-mock-${key}`] = jsonStr;
};

// Mock structures matching Soroban states
interface MockVault {
  id: number;
  creator: string;
  name: string;
  description: string;
  guardians: string[];
  approvalThreshold: number;
  isActive: boolean;
  createdAt: number;
}

interface MockDocument {
  id: number;
  vaultId: number;
  encryptedMetadata: string;
  ipfsHash: string;
  uploadedBy: string;
  uploadedAt: number;
  requiredAccess: number;
  releaseCondition: number;
  shares: Record<string, string>;
}

interface MockRequest {
  requestId: number;
  documentId: number;
  requester: string;
  approvedBy: string[];
  status: number;
  expiresAt: number;
  createdAt: number;
  beneficiaryShares: Record<string, string>;
}

interface MockInvite {
  guardian: string;
  vaultId: number;
  accepted: boolean;
  expiresAt: number;
}

const executeSorobanQuery = async (
  functionName: string,
  args: any[],
  contractAddressOverride?: string
): Promise<any> => {
  const sdk = await loadStellarSdk();
  const server = new sdk.rpc.Server(sorobanRpcUrl);
  const contractAddress = contractAddressOverride || getContractId();
  
  const sourceAddress = activeAccount || "GAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWHF";
  const sourceAccount = new sdk.Account(sourceAddress, "0");
  
  const scArgs = args.map(arg => {
    if (typeof arg === "string" && arg.startsWith("G") && arg.length === 56) {
      return sdk.nativeToScVal(new sdk.Address(arg));
    }
    return sdk.nativeToScVal(arg);
  });
  
  const op = sdk.Operation.invokeContractFunction({
    contract: contractAddress,
    function: functionName,
    args: scArgs,
  });
  
  const tx = new sdk.TransactionBuilder(sourceAccount, {
    fee: "100",
    networkPassphrase: sdk.Networks.TESTNET,
  })
    .addOperation(op)
    .setTimeout(30)
    .build();
    
  const simulation = await server.simulateTransaction(tx);
  if (sdk.rpc.Api.isSimulationError(simulation)) {
    throw new Error(`Simulation failed: ${simulation.error}`);
  }
  
  if (simulation.results && simulation.results.length > 0) {
    const result = simulation.results[0];
    if (result.retval) {
      return sdk.scValToNative(result.retval);
    }
  }
  return null;
};

const executeSorobanCall = async (
  functionName: string,
  args: any[],
  contractAddressOverride?: string
): Promise<any> => {
  if (!activeAccount) throw new Error("Wallet not connected");
  
  const sdk = await loadStellarSdk();
  const server = new sdk.rpc.Server(sorobanRpcUrl);
  const contractAddress = contractAddressOverride || getContractId();
  
  // 1. Fetch source account
  const sourceAccount = await server.getAccount(activeAccount);
  
  // 2. Convert arguments
  const scArgs = args.map(arg => {
    if (typeof arg === "string" && arg.startsWith("G") && arg.length === 56) {
      return sdk.nativeToScVal(new sdk.Address(arg));
    }
    if (Array.isArray(arg)) {
      return sdk.nativeToScVal(arg.map(item => {
        if (typeof item === "string" && item.startsWith("G") && item.length === 56) {
          return new sdk.Address(item);
        }
        return item;
      }));
    }
    return sdk.nativeToScVal(arg);
  });
  
  // 3. Build Operation & Base transaction
  const op = sdk.Operation.invokeContractFunction({
    contract: contractAddress,
    function: functionName,
    args: scArgs,
  });
  
  const tx = new sdk.TransactionBuilder(sourceAccount, {
    fee: "100",
    networkPassphrase: sdk.Networks.TESTNET,
  })
    .addOperation(op)
    .setTimeout(60)
    .build();
    
  // 4. Simulate transaction
  const simulation = await server.simulateTransaction(tx);
  if (sdk.rpc.Api.isSimulationError(simulation)) {
    throw new Error(`Simulation failed: ${simulation.error}`);
  }
  
  // 5. Sign auth entries (simulation entries) before transaction submission
  if (simulation.results && simulation.results.length > 0) {
    const result = simulation.results[0];
    if (result.auth && result.auth.length > 0) {
      if (activeWalletType === "demo" && demoKeypair) {
        for (let i = 0; i < result.auth.length; i++) {
          const entry = result.auth[i];
          if (typeof sdk.authorizeEntry === "function") {
            try {
              const latestLedger = await server.getLatestLedger();
              const validUntil = latestLedger.sequence + 120;
              result.auth[i] = await sdk.authorizeEntry(entry, demoKeypair, validUntil, sdk.Networks.TESTNET);
            } catch (err) {
              console.warn("Could not authorize entry with demoKeypair:", err);
            }
          }
        }
      } else if (activeWalletType !== "albedo") {
        const freighter = await loadFreighter();
        if (typeof freighter.signAuthEntry === "function") {
          for (let i = 0; i < result.auth.length; i++) {
            const entry = result.auth[i];
            const preimageXdr = entry.preimage().toXDR("base64");

            const signResponse = await freighter.signAuthEntry(preimageXdr);
            if (signResponse.error) {
              throw new Error(`Freighter auth entry signing failed: ${signResponse.error}`);
            }
            
            if (signResponse.signedAuthEntry) {
              const signedEntryXdr = signResponse.signedAuthEntry;
              const parsedEntry = sdk.xdr.SorobanAuthorizationEntry.fromXDR(signedEntryXdr, "base64");
              result.auth[i] = parsedEntry;
            }
          }
        }
      }
    }
  }
  
  // 6. Assemble the transaction with simulated data
  const preparedTx = sdk.rpc.assembleTransaction(tx, simulation).build();
  
  // 7. Request user signature for transaction envelope
  let signedXdr = "";
  if (activeWalletType === "demo" && demoKeypair) {
    preparedTx.sign(demoKeypair);
    signedXdr = preparedTx.toXDR();
  } else if (activeWalletType === "albedo") {
    signedXdr = await requestAlbedoTransaction(preparedTx.toXDR());
  } else {
    const freighter = await loadFreighter();
    if (typeof freighter.signTransaction !== "function") {
      throw new Error("Freighter wallet does not support signTransaction");
    }
    signedXdr = await freighter.signTransaction(preparedTx.toXDR(), {
      network: "TESTNET"
    });
  }

  if (!signedXdr) {
    throw new Error("Transaction signing rejected or failed");
  }
  
  // 8. Submit the transaction
  const signedTx = sdk.TransactionBuilder.fromXDR(signedXdr, sdk.Networks.TESTNET);
  const response = await server.sendTransaction(signedTx);
  if (response.status === "ERROR") {
    throw new Error(`Transaction submission failed: ${JSON.stringify(response.errorResult)}`);
  }
  
  // 9. Poll for status
  let pollAttempts = 0;
  while (pollAttempts < 30) {
    const txStatus = await server.getTransaction(response.hash);
    if (txStatus.status === "SUCCESS") {
      if (simulation.results && simulation.results.length > 0 && simulation.results[0].retval) {
        return sdk.scValToNative(simulation.results[0].retval);
      }
      return null;
    } else if (txStatus.status === "FAILED") {
      throw new Error(`Transaction execution failed: ${JSON.stringify(txStatus.resultXdr)}`);
    }
    const delayMs = typeof process !== "undefined" && process.env.NODE_ENV === "test" ? 1 : 1000;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    pollAttempts++;
  }
  
  throw new Error("Transaction polling timed out");
};

const syncProofOfLife = async (
  vaultId: number,
  evmVaultId: number,
  gidHash: string,
  evmOwner: string,
  timestamp: number,
  signature: string,
  recoveryId: number,
  relayer?: string
): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");
  if (relayer && relayer !== activeAccount) {
    throw new Error("Configured relayer does not match the connected Stellar account");
  }
  if (!isConfigured()) {
    throw new Error("Stellar contract is not configured for heartbeat relay");
  }

  await executeSorobanCall("sync_proof_of_life", [
    activeAccount,
    vaultId,
    evmVaultId,
    hexToBytes(gidHash),
    hexToBytes(evmOwner),
    timestamp,
    hexToBytes(signature),
    recoveryId,
  ]);
};

const bindCrossChainHeartbeat = async (
  vaultId: number,
  gidHash: string,
  evmOwner: string,
  relayer: string
): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");
  if (!isConfigured()) {
    throw new Error("Stellar contract is not configured for heartbeat binding");
  }

  await executeSorobanCall("bind_cross_chain_heartbeat", [
    activeAccount,
    vaultId,
    hexToBytes(gidHash),
    hexToBytes(evmOwner),
    relayer,
  ]);
};

const createVault = async (
  name: string,
  description: string,
  guardians: string[],
  approvalThreshold: number
): Promise<number> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  // If contract is set up, perform genuine Soroban call
  if (isConfigured()) {
    try {
      const vaultId = await executeSorobanCall("create_vault", [
        activeAccount,
        name,
        description,
        guardians,
        approvalThreshold,
      ]);
      return Number(vaultId);
    } catch (err) {
      console.error("Soroban create_vault failed:", err);
      throw err;
    }
  }

  // Fallback to Mock Database for instantaneous UI execution and debugging
  const vaults = getMockStorage<MockVault[]>("vaults", []);
  const nextId = vaults.length + 1;
  const newVault: MockVault = {
    id: nextId,
    creator: activeAccount,
    name,
    description,
    guardians: [activeAccount],
    approvalThreshold,
    isActive: true,
    createdAt: Math.floor(Date.now() / 1000),
  };

  vaults.push(newVault);
  saveMockStorage("vaults", vaults);

  // Add invites
  const invites = getMockStorage<MockInvite[]>("invites", []);
  for (const guardian of guardians) {
    if (guardian.toLowerCase() === activeAccount.toLowerCase()) continue;
    invites.push({
      guardian: guardian.trim(),
      vaultId: nextId,
      accepted: false,
      expiresAt: Math.floor(Date.now() / 1000) + 7 * 24 * 60 * 60,
    });
  }
  saveMockStorage("invites", invites);

  return nextId;
};

const getVault = async (vaultId: number): Promise<StellarVaultData | null> => {
  const vaults = getMockStorage<MockVault[]>("vaults", []);
  const vault = vaults.find((v) => v.id === vaultId);
  return vault || null;
};

const fetchVaultsForAccount = async (account: string): Promise<StellarVaultData[]> => {
  const vaults = getMockStorage<MockVault[]>("vaults", []);
  const target = account.toLowerCase();
  
  // Return vaults where user is a creator or active guardian
  return vaults
    .filter(
      (v) =>
        v.creator.toLowerCase() === target ||
        v.guardians.some((g) => g.toLowerCase() === target)
    )
    .map((v) => ({ ...v, network: "stellar" as const }));
};

const addDocument = async (
  vaultId: number,
  encryptedMetadata: string,
  ipfsHash: string,
  requiredAccess: number,
  releaseCondition = 0,
  guardiansList: string[] = [],
  shares: string[] = []
): Promise<number> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      const docId = await executeSorobanCall("add_document", [
        activeAccount,
        vaultId,
        encryptedMetadata,
        ipfsHash,
        requiredAccess,
        releaseCondition,
        guardiansList,
        shares
      ]);
      return Number(docId);
    } catch (err) {
      console.error("Soroban add_document failed:", err);
      throw err;
    }
  }

  const docs = getMockStorage<MockDocument[]>("documents", []);
  const nextId = docs.length + 1;

  const sharesMap: Record<string, string> = {};
  guardiansList.forEach((guardian, idx) => {
    sharesMap[guardian] = shares[idx];
  });

  const newDoc: MockDocument = {
    id: nextId,
    vaultId,
    encryptedMetadata,
    ipfsHash,
    uploadedBy: activeAccount,
    uploadedAt: Math.floor(Date.now() / 1000),
    requiredAccess,
    releaseCondition,
    shares: sharesMap,
  };

  docs.push(newDoc);
  saveMockStorage("documents", docs);
  return nextId;
};

const fetchDocumentsForVaults = async (vaultIds: number[]): Promise<StellarDocumentData[]> => {
  const docs = getMockStorage<MockDocument[]>("documents", []);
  const set = new Set(vaultIds);
  return docs.filter((d) => set.has(d.vaultId));
};

const requestAccess = async (documentId: number): Promise<number> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      const requestId = await executeSorobanCall("request_access", [
        activeAccount,
        documentId
      ]);
      return Number(requestId);
    } catch (err) {
      console.error("Soroban request_access failed:", err);
      throw err;
    }
  }

  const requests = getMockStorage<MockRequest[]>("requests", []);
  const nextId = requests.length + 1;

  const newReq: MockRequest = {
    requestId: nextId,
    documentId,
    requester: activeAccount,
    approvedBy: [],
    status: 0, // Pending
    expiresAt: Math.floor(Date.now() / 1000) + 3 * 24 * 60 * 60,
    createdAt: Math.floor(Date.now() / 1000),
    beneficiaryShares: {},
  };

  requests.push(newReq);
  saveMockStorage("requests", requests);
  return nextId;
};

const approveAccess = async (requestId: number, encryptedShareForBeneficiary?: string): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      await executeSorobanCall("approve_access", [
        activeAccount,
        requestId,
        encryptedShareForBeneficiary || null
      ]);
      return;
    } catch (err) {
      console.error("Soroban approve_access failed:", err);
      throw err;
    }
  }

  const requests = getMockStorage<MockRequest[]>("requests", []);
  const reqIdx = requests.findIndex((r) => r.requestId === requestId);
  if (reqIdx === -1) throw new Error("Request not found");

  const req = requests[reqIdx];
  if (req.approvedBy.includes(activeAccount)) {
    throw new Error("Already approved");
  }

  req.approvedBy.push(activeAccount);
  if (encryptedShareForBeneficiary) {
    req.beneficiaryShares[activeAccount] = encryptedShareForBeneficiary;
  }

  // Fetch vault approval threshold
  const docs = getMockStorage<MockDocument[]>("documents", []);
  const doc = docs.find((d) => d.id === req.documentId);
  if (doc) {
    const vaults = getMockStorage<MockVault[]>("vaults", []);
    const vault = vaults.find((v) => v.id === doc.vaultId);
    if (vault && req.approvedBy.length >= vault.approvalThreshold) {
      req.status = 1; // Approved
    }
  }

  requests[reqIdx] = req;
  saveMockStorage("requests", requests);
};

const registerGuardianBLSKey = async (
  vaultId: number,
  blsPublicKey: string,
  proofOfPossession: string
): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      await executeSorobanCall("register_guardian_bls_key", [
        activeAccount,
        vaultId,
        hexToBytes(blsPublicKey),
        hexToBytes(proofOfPossession),
      ]);
      return;
    } catch (err) {
      console.error("Soroban register_guardian_bls_key failed:", err);
      throw err;
    }
  }

  const blsKeys = getMockStorage<Record<string, { publicKey: string; proofOfPossession: string; registered: boolean }>>("stellar_bls_keys", {});
  const keyIdentifier = `${vaultId}_${activeAccount.toLowerCase()}`;
  blsKeys[keyIdentifier] = {
    publicKey: blsPublicKey,
    proofOfPossession,
    registered: true,
  };
  saveMockStorage("stellar_bls_keys", blsKeys);
};

const getGuardianBLSKey = async (
  vaultId: number,
  guardianAddress: string
): Promise<{ blsPublicKey: string; proofOfPossession: string; isRegistered: boolean } | null> => {
  const blsKeys = getMockStorage<Record<string, { publicKey: string; proofOfPossession: string; registered: boolean }>>("stellar_bls_keys", {});
  const keyIdentifier = `${vaultId}_${guardianAddress.toLowerCase()}`;
  const info = blsKeys[keyIdentifier];
  if (!info) return null;
  return {
    blsPublicKey: info.publicKey,
    proofOfPossession: info.proofOfPossession,
    isRegistered: info.registered,
  };
};

const approveAccessBLS = async (
  requestId: number,
  guardianAddresses: string[],
  aggregatedSignature: string,
  aggregatedPublicKey: string,
  encryptedShares: string[] = []
): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      await executeSorobanCall("approve_access_bls", [
        requestId,
        guardianAddresses,
        hexToBytes(aggregatedSignature),
        hexToBytes(aggregatedPublicKey),
        encryptedShares,
      ]);
      return;
    } catch (err) {
      console.error("Soroban approve_access_bls failed:", err);
      throw err;
    }
  }

  const requests = getMockStorage<MockRequest[]>("requests", []);
  const reqIdx = requests.findIndex((r) => r.requestId === requestId);
  if (reqIdx === -1) throw new Error("Request not found");

  const req = requests[reqIdx];
  for (let i = 0; i < guardianAddresses.length; i++) {
    const guardian = guardianAddresses[i];
    if (!req.approvedBy.includes(guardian)) {
      req.approvedBy.push(guardian);
    }
    if (i < encryptedShares.length && encryptedShares[i]) {
      req.beneficiaryShares[guardian] = encryptedShares[i];
    }
  }

  req.status = 1; // Approved
  requests[reqIdx] = req;
  saveMockStorage("requests", requests);
};

const fetchPendingApprovalsForGuardian = async (
  guardianAddress: string
): Promise<StellarPendingApprovalData[]> => {
  const requests = getMockStorage<MockRequest[]>("requests", []);
  const docs = getMockStorage<MockDocument[]>("documents", []);
  const vaults = getMockStorage<MockVault[]>("vaults", []);
  const target = guardianAddress.toLowerCase();

  const pending: StellarPendingApprovalData[] = [];

  for (const req of requests) {
    if (req.status !== 0) continue; // Not pending
    if (req.approvedBy.some((a) => a.toLowerCase() === target)) continue; // Already approved by us

    const doc = docs.find((d) => d.id === req.documentId);
    if (!doc) continue;

    const vault = vaults.find((v) => v.id === doc.vaultId);
    if (!vault) continue;

    // Check if the user is a guardian of this vault
    const isGuardian = vault.guardians.some((g) => g.toLowerCase() === target);
    if (!isGuardian) continue;

    pending.push({
      requestId: req.requestId,
      documentId: req.documentId,
      vaultId: vault.id,
      vaultName: vault.name,
      requester: req.requester,
      createdAt: req.createdAt,
      expiresAt: req.expiresAt,
    });
  }

  return pending;
};

const getEncryptedGuardianShare = async (documentId: number, guardian: string): Promise<string> => {
  const docs = getMockStorage<MockDocument[]>("documents", []);
  const doc = docs.find((d) => d.id === documentId);
  return doc?.shares?.[guardian] || "";
};

const getBeneficiaryKeyShare = async (requestId: number, guardian: string): Promise<string> => {
  const requests = getMockStorage<MockRequest[]>("requests", []);
  const req = requests.find((r) => r.requestId === requestId);
  return req?.beneficiaryShares?.[guardian] || "";
};

const getPendingInvites = async (account: string): Promise<MockInvite[]> => {
  const invites = getMockStorage<MockInvite[]>("invites", []);
  const target = account.toLowerCase();
  return invites.filter((inv) => inv.guardian.toLowerCase() === target && !inv.accepted);
};

const acceptGuardianInvite = async (vaultId: number): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      await executeSorobanCall("accept_guardian_invite", [
        activeAccount,
        vaultId
      ]);
      return;
    } catch (err) {
      console.error("Soroban accept_guardian_invite failed:", err);
      throw err;
    }
  }

  const invites = getMockStorage<MockInvite[]>("invites", []);
  const invIdx = invites.findIndex(
    (inv) => inv.vaultId === vaultId && inv.guardian.toLowerCase() === activeAccount!.toLowerCase()
  );

  if (invIdx !== -1) {
    invites[invIdx].accepted = true;
    saveMockStorage("invites", invites);
  }

  const vaults = getMockStorage<MockVault[]>("vaults", []);
  const vaultIdx = vaults.findIndex((v) => v.id === vaultId);
  if (vaultIdx !== -1) {
    if (!vaults[vaultIdx].guardians.includes(activeAccount)) {
      vaults[vaultIdx].guardians.push(activeAccount);
      saveMockStorage("vaults", vaults);
    }
  }
};

const registerPublicKey = async (publicKey: string): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      await executeSorobanCall("register_public_key", [
        activeAccount,
        publicKey
      ]);
      return;
    } catch (err) {
      console.error("Soroban register_public_key failed:", err);
      throw err;
    }
  }

  const pubKeys = getMockStorage<Record<string, string>>("public_keys", {});
  pubKeys[activeAccount] = publicKey;
  saveMockStorage("public_keys", pubKeys);
};

const getUserPublicKey = async (user: string): Promise<string> => {
  if (isConfigured()) {
    try {
      const pubKey = await executeSorobanQuery("get_public_key", [
        user
      ]);
      return pubKey || "";
    } catch (err) {
      console.error("Soroban get_public_key failed:", err);
    }
  }

  const pubKeys = getMockStorage<Record<string, string>>("public_keys", {});
  return pubKeys[user] || "";
};

interface MockToken {
  tokenId: number;
  owner: string;
  vaultId: number;
  tokenURI: string;
  mintedAt: number;
}

const fetchUserTokens = async (account: string): Promise<StellarTokenData[]> => {
  if (!account) return [];
  const tokens = getMockStorage<MockToken[]>("tokens", []);
  const target = account.toLowerCase();
  return tokens
    .filter((t) => t.owner.toLowerCase() === target)
    .map((t) => ({
      tokenId: t.tokenId,
      owner: t.owner,
      vaultId: t.vaultId,
      tokenURI: t.tokenURI || "",
      mintedAt: t.mintedAt || null,
    }));
};

const hasVaultToken = async (account: string, vaultId: number): Promise<boolean> => {
  if (!account || !vaultId || vaultId <= 0) return false;
  try {
    const tokens = getMockStorage<MockToken[]>("tokens", []);
    const target = account.toLowerCase();
    const hasMockToken = tokens.some(
      (t) => t.vaultId === vaultId && t.owner.toLowerCase() === target
    );
    if (hasMockToken) return true;

    if (isConfigured() && (typeof process === "undefined" || process.env.NODE_ENV !== "test")) {
      const hasToken = await executeSorobanQuery("has_vault_token", [
        account,
        vaultId
      ]);
      return !!hasToken;
    }
    return false;
  } catch (error) {
    console.error("Soroban token query failed:", error);
    return false;
  }
};

const mintAccessToken = async (
  vaultId: number,
  to: string,
  tokenURI: string
): Promise<number> => {
  if (isConfigured() && activeAccount) {
    try {
      const tokenId = await executeSorobanCall("mint_access_token", [
        activeAccount,
        vaultId,
        to,
        tokenURI
      ]);
      return Number(tokenId);
    } catch (err) {
      console.error("Soroban mint_access_token failed:", err);
      throw err;
    }
  }

  const tokens = getMockStorage<MockToken[]>("tokens", []);
  const nextId = tokens.length + 1;
  const newToken: MockToken = {
    tokenId: nextId,
    owner: to,
    vaultId,
    tokenURI,
    mintedAt: Math.floor(Date.now() / 1000),
  };
  tokens.push(newToken);
  saveMockStorage("tokens", tokens);
  return nextId;
};

const getTotalSupply = async (): Promise<number> => {
  const tokens = getMockStorage<MockToken[]>("tokens", []);
  return tokens.length;
};

const burnAccessToken = async (tokenId: number): Promise<void> => {
  let tokens = getMockStorage<MockToken[]>("tokens", []);
  tokens = tokens.filter((t) => t.tokenId !== tokenId);
  saveMockStorage("tokens", tokens);
};

interface MockKeeperAuthorization {
  keeper: string;
  expiresAt: number;
}

interface MockEmergencyUnlock {
  emergencyMode: boolean;
  cycle: number;
  requestedAt: number;
  requested: boolean;
  fulfilled: boolean;
  unlockAt: number;
  unlockBlock: number;
}

const MOCK_EMERGENCY_CONFIRMATION_MS = 15_000;
const MOCK_EMERGENCY_BASE_DELAY_SEC = 600;
const MOCK_EMERGENCY_WINDOW_SEC = 3600;

const loadEmergencyUnlocks = (): Record<number, MockEmergencyUnlock> =>
  getMockStorage<Record<number, MockEmergencyUnlock>>("emergency_unlocks", {});

const setEmergencyMode = async (vaultId: number, enabled: boolean): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    await executeSorobanCall("set_emergency_mode", [activeAccount, vaultId, enabled]);
    return;
  }

  const schedules = loadEmergencyUnlocks();
  if (enabled) {
    const existing = schedules[vaultId];
    if (existing?.requested && !existing.fulfilled) {
      throw new Error("Emergency unlock delay already pending");
    }
    schedules[vaultId] = {
      emergencyMode: true,
      cycle: (existing?.cycle || 0) + 1,
      requestedAt: Date.now(),
      requested: true,
      fulfilled: false,
      unlockAt: 0,
      unlockBlock: 0,
    };
  } else if (schedules[vaultId]) {
    schedules[vaultId] = {
      ...schedules[vaultId],
      emergencyMode: false,
      requested: false,
      fulfilled: false,
      unlockAt: 0,
      unlockBlock: 0,
    };
  } else {
    schedules[vaultId] = {
      emergencyMode: false,
      cycle: 0,
      requestedAt: 0,
      requested: false,
      fulfilled: false,
      unlockAt: 0,
      unlockBlock: 0,
    };
  }
  saveMockStorage("emergency_unlocks", schedules);
};

const fulfillEmergencyUnlockDelay = async (vaultId: number): Promise<void> => {
  if (isConfigured()) {
    await executeSorobanCall("fulfill_emergency_unlock_delay", [vaultId]);
    return;
  }

  const schedules = loadEmergencyUnlocks();
  const schedule = schedules[vaultId];
  if (!schedule?.emergencyMode) {
    throw new Error("Emergency mode is not enabled");
  }
  if (!schedule.requested || schedule.fulfilled) {
    throw new Error("No emergency unlock request");
  }
  if (Date.now() < schedule.requestedAt + MOCK_EMERGENCY_CONFIRMATION_MS) {
    throw new Error("Emergency unlock confirmations not met");
  }

  const jitter = Math.floor(Math.random() * MOCK_EMERGENCY_WINDOW_SEC);
  const nowSec = Math.floor(Date.now() / 1000);
  schedules[vaultId] = {
    ...schedule,
    fulfilled: true,
    unlockAt: nowSec + MOCK_EMERGENCY_BASE_DELAY_SEC + jitter,
    unlockBlock: nowSec + 256 + Math.floor(jitter / 5),
  };
  saveMockStorage("emergency_unlocks", schedules);
};

const getEmergencyUnlockSchedule = async (
  vaultId: number
): Promise<{
  requested: boolean;
  fulfilled: boolean;
  unlockAt: number;
  unlockBlock: number;
}> => {
  const empty = { requested: false, fulfilled: false, unlockAt: 0, unlockBlock: 0 };

  if (isConfigured()) {
    try {
      const result = await executeSorobanQuery("get_emergency_unlock_schedule", [vaultId]);
      if (!result) return empty;
      return {
        requested: true,
        fulfilled: Boolean(result.fulfilled),
        unlockAt: Number(result.unlock_at ?? 0),
        unlockBlock: Number(result.unlock_sequence ?? 0),
      };
    } catch (err) {
      console.error("Soroban get_emergency_unlock_schedule failed:", err);
      return empty;
    }
  }

  const schedule = loadEmergencyUnlocks()[vaultId];
  if (!schedule) return empty;
  return {
    requested: Boolean(schedule.requested),
    fulfilled: Boolean(schedule.fulfilled),
    unlockAt: Number(schedule.unlockAt || 0),
    unlockBlock: Number(schedule.unlockBlock || 0),
  };
};

const getReleaseState = async (
  vaultId: number
): Promise<{
  emergencyMode: boolean;
  inactivityPeriod: number;
  lastProofOfLife: number;
  postDeathUnlocked: boolean;
}> => {
  const fallback = {
    emergencyMode: false,
    inactivityPeriod: 30 * 24 * 60 * 60,
    lastProofOfLife: 0,
    postDeathUnlocked: false,
  };

  if (isConfigured()) {
    try {
      const result = await executeSorobanQuery("get_release_state", [vaultId]);
      if (!result) return fallback;
      return {
        emergencyMode: Boolean(result.emergency_mode),
        inactivityPeriod: Number(result.inactivity_period ?? fallback.inactivityPeriod),
        lastProofOfLife: Number(result.last_proof_of_life ?? 0),
        postDeathUnlocked: false,
      };
    } catch (err) {
      console.error("Soroban get_release_state failed:", err);
      return fallback;
    }
  }

  const schedule = loadEmergencyUnlocks()[vaultId];
  return {
    ...fallback,
    emergencyMode: Boolean(schedule?.emergencyMode),
  };
};

/**
 * Authorize a Web3 Keeper (Chainlink Automation / Gelato) to relay proof-of-life
 * heartbeats for `vaultId` until `expiresAt`. Soroban's native `require_auth`
 * already separates who authorizes an action from who submits/pays for the
 * transaction, so — unlike the EVM side — this needs no off-chain signature
 * scheme of its own: it's a normal owner-signed contract call.
 */
const authorizeKeeper = async (
  vaultId: number,
  keeper: string,
  expiresAt: number
): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      await executeSorobanCall("authorize_keeper", [activeAccount, vaultId, keeper, expiresAt]);
      return;
    } catch (err) {
      console.error("Soroban authorize_keeper failed:", err);
      throw err;
    }
  }

  const authorizations = getMockStorage<Record<number, MockKeeperAuthorization>>(
    "keeper_authorizations",
    {}
  );
  authorizations[vaultId] = { keeper, expiresAt };
  saveMockStorage("keeper_authorizations", authorizations);
};

const revokeKeeperAuthorization = async (vaultId: number): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (isConfigured()) {
    try {
      await executeSorobanCall("revoke_keeper", [activeAccount, vaultId]);
      return;
    } catch (err) {
      console.error("Soroban revoke_keeper failed:", err);
      throw err;
    }
  }

  const authorizations = getMockStorage<Record<number, MockKeeperAuthorization>>(
    "keeper_authorizations",
    {}
  );
  delete authorizations[vaultId];
  saveMockStorage("keeper_authorizations", authorizations);
};

const getKeeperAuthorization = async (
  vaultId: number
): Promise<StellarKeeperAuthorizationData | null> => {
  if (isConfigured()) {
    try {
      const result = await executeSorobanQuery("get_keeper_authorization", [vaultId]);
      if (!result) return null;
      return {
        keeper: String(result.keeper ?? result[0]),
        expiresAt: Number(result.expires_at ?? result[1]),
      };
    } catch (err) {
      console.error("Soroban get_keeper_authorization failed:", err);
      return null;
    }
  }

  const authorizations = getMockStorage<Record<number, MockKeeperAuthorization>>(
    "keeper_authorizations",
    {}
  );
  return authorizations[vaultId] || null;
};

/**
 * Web3 Keeper relay of a proof-of-life heartbeat, submitted using the keeper's
 * own connected account as both the transaction source and the `require_auth`
 * signer — gated by a prior {authorizeKeeper} grant on-chain.
 */
const relayProofOfLifeAsKeeper = async (vaultId: number): Promise<void> => {
  if (!activeAccount) throw new Error("Wallet not connected");

  if (!isConfigured()) {
    throw new Error("Proof-of-life relay requires a configured Soroban contract.");
  }

  try {
    await executeSorobanCall("prove_life_by_keeper", [activeAccount, vaultId]);
  } catch (err) {
    console.error("Soroban prove_life_by_keeper failed:", err);
    throw err;
  }
};

const saveCrossChainBindingMock = (
  evmAddress: string,
  stellarAddress: string,
  publicKey?: string
): void => {
  const normEvm = evmAddress.toLowerCase().trim();
  const normStellar = stellarAddress.trim();

  const evmToStellar = getMockStorage<Record<string, string>>("cross_evm_to_stellar", {});
  const stellarToEvm = getMockStorage<Record<string, string>>("cross_stellar_to_evm", {});
  const evmToPubkey = getMockStorage<Record<string, string>>("cross_evm_to_pubkey", {});

  evmToStellar[normEvm] = normStellar;
  stellarToEvm[normStellar] = normEvm;

  saveMockStorage("cross_evm_to_stellar", evmToStellar);
  saveMockStorage("cross_stellar_to_evm", stellarToEvm);

  if (publicKey) {
    evmToPubkey[normEvm] = publicKey;
    saveMockStorage("cross_evm_to_pubkey", evmToPubkey);

    const pubKeys = getMockStorage<Record<string, string>>("public_keys", {});
    pubKeys[normStellar] = publicKey;
    saveMockStorage("public_keys", pubKeys);
  }
};

const registerCrossChainIdentity = async (
  stellarAddress: string,
  evmAddress: string,
  publicKey?: string
): Promise<void> => {
  saveCrossChainBindingMock(evmAddress, stellarAddress, publicKey);
};

const resolveEvmToStellar = async (evmAddress: string): Promise<string | null> => {
  const normEvm = evmAddress.toLowerCase().trim();

  if (isIdentityRegistryConfigured()) {
    try {
      const binding = normalizeBindingResult(
        await executeSorobanQuery(
          "resolve_evm_to_stellar",
          [hexToBytes(normEvm)],
          getIdentityRegistryContractId()
        )
      );
      if (binding?.stellarPublicKey) {
        return encodeStellarPublicKey(binding.stellarPublicKey);
      }
    } catch (err) {
      console.error("Soroban resolve_evm_to_stellar failed:", err);
    }
  }

  const evmToStellar = getMockStorage<Record<string, string>>("cross_evm_to_stellar", {});
  return evmToStellar[normEvm] || null;
};

const resolveStellarToEvm = async (stellarAddress: string): Promise<string | null> => {
  const normStellar = stellarAddress.trim();

  if (isIdentityRegistryConfigured()) {
    try {
      const sdk = await loadStellarSdk();
      const pubkey = sdk.StrKey.decodeEd25519PublicKey(normStellar);
      const binding = normalizeBindingResult(
        await executeSorobanQuery(
          "resolve_stellar_to_evm",
          [Uint8Array.from(pubkey)],
          getIdentityRegistryContractId()
        )
      );
      if (binding?.evmAddress) {
        return binding.evmAddress.toLowerCase();
      }
    } catch (err) {
      console.error("Soroban resolve_stellar_to_evm failed:", err);
    }
  }

  const stellarToEvm = getMockStorage<Record<string, string>>("cross_stellar_to_evm", {});
  return stellarToEvm[normStellar] || null;
};

// ---------------------------------------------------------------------------
// Dual-signed identity binding (MetaMask + Freighter)
// ---------------------------------------------------------------------------

export interface CrossChainIdentityBinding {
  evmAddress: string;
  stellarAddress: string;
  stellarPublicKey: string;
  timestamp: number;
  /** 65-byte EIP-191 signature (r || s || v) from MetaMask personal_sign. */
  evmSignature: string;
  /** 64-byte Ed25519 signature over the message hash from Freighter signBlob. */
  stellarSignature: string;
}

/**
 * Split a 65-byte EIP-191 signature into (r || s) and the recovery id.
 * Accepts v as 27/28 or 0/1.
 */
const splitEvmSignature = (signatureHex: string): { rs: string; recoveryId: number } => {
  const bytes = hexToBytes(signatureHex);
  if (bytes.length !== 65) throw new Error("EVM signature must be 65 bytes");
  const v = bytes[64];
  const recoveryId = v === 27 || v === 28 ? v - 27 : v;
  if (recoveryId !== 0 && recoveryId !== 1) throw new Error("Invalid EVM signature recovery id");
  return { rs: `0x${bytesToHex(bytes.slice(0, 64))}`, recoveryId };
};

/**
 * Record a dual-signed EVM <-> Stellar identity binding.
 *
 * When the Soroban identity registry is configured the binding is submitted
 * on-chain (both signatures are verified by the contract and invalid or
 * single-signed requests revert). Otherwise the signatures are verified
 * locally and the binding is recorded in the mock store.
 */
const bindIdentity = async (binding: CrossChainIdentityBinding): Promise<void> => {
  const { evmAddress, stellarAddress, stellarPublicKey, timestamp, evmSignature, stellarSignature } = binding;
  if (!evmAddress || !stellarAddress || !stellarPublicKey) {
    throw new Error("Missing binding addresses");
  }
  if (!evmSignature || !stellarSignature) {
    throw new Error("Both EVM and Stellar signatures are required");
  }
  const evmBytes = hexToBytes(evmAddress);
  const pkBytes = hexToBytes(stellarPublicKey);
  if (evmBytes.length !== 20) throw new Error("Invalid EVM address");
  if (pkBytes.length !== 32) throw new Error("Invalid Stellar public key");

  if (isIdentityRegistryConfigured()) {
    const { rs, recoveryId } = splitEvmSignature(evmSignature);
    try {
      await executeSorobanCall(
        "bind_identity",
        [
          evmBytes,
          pkBytes,
          Math.floor(timestamp),
          hexToBytes(rs),
          recoveryId,
          hexToBytes(stellarSignature),
        ],
        getIdentityRegistryContractId()
      );
      return;
    } catch (err) {
      console.error("Soroban bind_identity failed:", err);
      throw err;
    }
  }

  // Fallback: verify both signatures locally before recording.
  const messageHash = await buildIdentityBindingMessageHash(evmAddress, stellarAddress, timestamp);
  const ethersMod = await loadEthers();
  const recovered = ethersMod.verifyMessage(ethersMod.getBytes(messageHash), evmSignature);
  if (recovered.toLowerCase() !== evmAddress.toLowerCase()) {
    throw new Error("Invalid EVM signature for identity binding");
  }

  const nacl = await loadTweetNacl();
  const stellarOk = nacl.sign.detached.verify(
    ethersMod.getBytes(messageHash),
    hexToBytes(stellarSignature),
    pkBytes
  );
  if (!stellarOk) {
    throw new Error("Invalid Stellar signature for identity binding");
  }

  saveCrossChainBindingMock(evmAddress, stellarAddress, stellarPublicKey);
};

/**
 * Sign the binding message hash with MetaMask (EIP-191 personal_sign).
 * @returns the 65-byte signature and the recovery id (0/1).
 */
const signIdentityBindingWithMetaMask = async (
  messageHash: string,
  signer?: { signMessage: (message: Uint8Array) => Promise<string> }
): Promise<{ signature: string; recoveryId: number }> => {
  const ethersMod = await loadEthers();
  let activeSigner: { signMessage: (message: Uint8Array) => Promise<string> };
  if (signer) {
    activeSigner = signer;
  } else {
    const eth = (window as any).ethereum;
    if (typeof window === "undefined" || !eth) {
      throw new Error("MetaMask wallet is not available");
    }
    const provider = new ethersMod.BrowserProvider(eth);
    activeSigner = await provider.getSigner();
  }
  const signature = await activeSigner.signMessage(ethersMod.getBytes(messageHash));
  const parsed = ethersMod.Signature.from(signature);
  return { signature, recoveryId: parsed.yParity };
};

/**
 * Sign the binding message hash with Freighter (Ed25519 signBlob).
 * @returns the 64-byte Ed25519 signature as 0x-prefixed hex.
 */
const signIdentityBindingWithFreighter = async (messageHash: string): Promise<string> => {
  if (activeWalletType === "demo" && demoKeypair) {
    const rawHashBytes = hexToBytes(messageHash.startsWith("0x") ? messageHash.slice(2) : messageHash);
    const signature = demoKeypair.sign(rawHashBytes);
    return `0x${bytesToHex(signature)}`;
  }
  const freighter = await loadFreighter();
  if (typeof freighter.signBlob !== "function") {
    throw new Error("Freighter signBlob is not available");
  }
  const blob = bytesToBase64(hexToBytes(messageHash));
  const signed = await freighter.signBlob(blob);
  if (!signed) {
    throw new Error("Freighter signing was rejected");
  }
  return `0x${bytesToHex(base64ToBytes(signed))}`;
};

const resolveEvmToPublicKey = async (evmAddress: string): Promise<string | null> => {
  const normEvm = evmAddress.toLowerCase().trim();
  const evmToPubkey = getMockStorage<Record<string, string>>("cross_evm_to_pubkey", {});
  if (evmToPubkey[normEvm]) {
    return evmToPubkey[normEvm];
  }

  // Fallback: resolve via Stellar address -> public key
  const stellarAddr = await resolveEvmToStellar(normEvm);
  if (stellarAddr) {
    const pubKey = await getUserPublicKey(stellarAddr);
    if (pubKey) return pubKey;
  }

  return null;
};

export const stellarService = {
  initialize,
  clear,
  getAccount,
  connectWallet,
  connectDemoWallet,
  connectAlbedoWallet,
  getWalletType,
  getDemoKeypair,
  isMobileDevice,
  getActiveNetwork,
  getNetwork,
  getRpcUrl,
  getContractId,
  subscribeToWalletChanges,
  createVault,
  getVault,
  fetchVaultsForAccount,
  addDocument,
  fetchDocumentsForVaults,
  requestAccess,
  approveAccess,
  registerGuardianBLSKey,
  getGuardianBLSKey,
  approveAccessBLS,
  fetchPendingApprovalsForGuardian,
  getEncryptedGuardianShare,
  getBeneficiaryKeyShare,
  getPendingInvites,
  acceptGuardianInvite,
  registerPublicKey,
  getUserPublicKey,
  fetchUserTokens,
  hasVaultToken,
  mintAccessToken,
  burnAccessToken,
  getTotalSupply,
  registerCrossChainIdentity,
  resolveEvmToStellar,
  resolveStellarToEvm,
  resolveEvmToPublicKey,
  authorizeKeeper,
  revokeKeeperAuthorization,
  getKeeperAuthorization,
  relayProofOfLifeAsKeeper,
  setEmergencyMode,
  fulfillEmergencyUnlockDelay,
  getEmergencyUnlockSchedule,
  getReleaseState,
  buildIdentityBindingMessageHash,
  signIdentityBindingWithMetaMask,
  signIdentityBindingWithFreighter,
  bindIdentity,
  syncProofOfLife,
  bindCrossChainHeartbeat,
  getIdentityRegistryContractId,
  isConfigured,
  setMockStellarSdk,
  setMockFreighter,
  invokeSorobanContract: executeSorobanCall,
};

declare global {
  interface Window {
    freighterApi?: {
      listen?: (callback: (event: StellarWalletChangeEvent) => void, opts?: { network?: string }) => unknown;
    };
    freighter?: {
      listen?: (callback: (event: StellarWalletChangeEvent) => void, opts?: { network?: string }) => unknown;
    };
  }
}
